import { SquareArrowOutUpRight } from 'lucide-react'

import type { QuotingProspect } from 'utils/web_api.schemas'

import { ProspectOwnerLink } from './ProspectOwnerLink'
import { formatFileSize, getFormattedDate } from '../../utils'
import { SkeletonItem } from '../reusable/SkeletonItem'

export function AdminProspectInfo ({
  prospect,
}: {
  prospect?: QuotingProspect | null;
}) {
  return (
    <div className='flex flex-col'>
      <div className='flex justify-between items-start p-4 bg-white rounded-lg border border-thatch-gray-border mb-4'>
        <dl className='flex gap-x-8 text-sm text-ds-text-default flex-wrap'>
          <div className='flex flex-col'>
            <dt className='font-semibold'>Prospect Email</dt>
            <dd>{prospect ? (prospect.email ?? '—') : <SkeletonItem />}</dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>Owner</dt>
            <dd>
              {prospect
                ? (
                  <ProspectOwnerLink prospect={prospect} />
                  )
                : (
                  <SkeletonItem />
                  )}
            </dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>Date created</dt>
            <dd>
              {prospect
                ? (
                    getFormattedDate(prospect.created_at)
                  )
                : (
                  <SkeletonItem />
                  )}
            </dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>EIN</dt>
            <dd>{prospect ? (prospect.ein ?? '—') : <SkeletonItem />}</dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>Number of employees</dt>
            <dd>{prospect ? (prospect.employee_count?.toString() ?? '—') : <SkeletonItem />}</dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>Insurance effective start date</dt>
            <dd>
              {prospect
                ? (
                    prospect.health_insurance_effective_date
                      ? getFormattedDate(prospect.health_insurance_effective_date)
                      : '—'
                  )
                : (
                  <SkeletonItem />
                  )}
            </dd>
          </div>
        </dl>

        {prospect?.external_link && (
          <a
            className='btn-link'
            href={prospect.external_link}
            target='_blank'
            rel='noreferrer'
          >
            Link to Salesforce Opportunity Page
            <SquareArrowOutUpRight size={18} />
          </a>
        )}
      </div>

      <div className='flex justify-between items-start p-4 bg-white rounded-lg border border-thatch-gray-border mb-4'>
        <dl className='flex gap-x-8 text-sm text-ds-text-default flex-wrap'>
          <div className='flex flex-col'>
            <dt className='font-semibold'>DBA</dt>
            <dd>{prospect ? (prospect.dba ?? '—') : <SkeletonItem />}</dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>Address</dt>
            <dd>
              {prospect
                ? (
                  <div>
                    {prospect.address_line1 && <div>{prospect.address_line1}</div>}
                    {prospect.address_line2 && <div>{prospect.address_line2}</div>}
                    {(prospect.city || prospect.state || prospect.zip) && (
                      <div>
                        {[prospect.city, prospect.state, prospect.zip]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    )}
                    {!prospect.address_line1 && !prospect.city && <div>—</div>}
                  </div>
                  )
                : (
                  <SkeletonItem />
                  )}
            </dd>
          </div>

          <div className='flex flex-col'>
            <dt className='font-semibold'>Phone</dt>
            <dd>{prospect ? (prospect.phone_number ?? '—') : <SkeletonItem />}</dd>
          </div>
        </dl>
      </div>

      {/* Documents Table */}
      {(() => {
        // Collect all files from different upload types
        const allFiles = [
          ...(prospect?.broker_uploads || []).map(file => ({ ...file, type: 'Broker Upload' })),
          ...(prospect?.census_upload ? [{ ...prospect.census_upload, type: 'Census Upload' }] : []),
          ...(prospect?.sbcs_uploads || []).map(file => ({ ...file, type: 'SBC Upload' })),
          ...(prospect?.other_file_uploads || []).map(file => ({ ...file, type: 'Other File' })),
        ]

        if (allFiles.length === 0) return null

        return (
          <div className='bg-white rounded-lg border border-thatch-gray-border p-4'>
            <h3 className='font-semibold text-sm text-ds-text-default mb-4'>
              Documents
            </h3>
            <div className='overflow-x-auto'>
              <table className='table-auto w-full divide-y divide-gray-300'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Type
                    </th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Name
                    </th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Size
                    </th>
                    <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Uploaded At
                    </th>
                    <th className='px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200 bg-white'>
                  {allFiles.map((upload) => (
                    <tr key={upload.id}>
                      <td className='px-3 py-2 text-sm text-gray-700 font-medium'>
                        {upload.type}
                      </td>
                      <td className='px-3 py-2 text-sm text-gray-900'>
                        {upload.filename}
                      </td>
                      <td className='px-3 py-2 text-sm text-gray-500'>
                        {formatFileSize(upload.byte_size)}
                      </td>
                      <td className='px-3 py-2 text-sm text-gray-500'>
                        {getFormattedDate(upload.created_at)}
                      </td>
                      <td className='px-3 py-2 text-sm'>
                        <div className='flex flex-row-reverse gap-6'>
                          <a
                            href={upload.download_url}
                            target='_blank'
                            rel='noreferrer'
                            className='text-thatch-600 hover:text-thatch-700 underline'
                          >
                            Download
                          </a>
                          <a
                            href={upload.view_url}
                            target='_blank'
                            rel='noreferrer'
                            className='text-thatch-600 hover:text-thatch-700 underline'
                          >
                            View
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
